// Live generation streams, held outside the React tree (same pattern as chatStreamStore.ts).
//
// A generation stream survives navigation: the state lives in this module singleton.
// Only components that subscribe re-render, not the whole app.

import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { draftKeys } from "./draftApi";
import { streamGenerate, type GenerationCitation } from "./generationApi";

export interface GenerationState {
  pending: boolean;
  streamingText: string;
  error: string | null;
  statusMessage: string | null;
  citations: GenerationCitation[] | null;
}

const IDLE: GenerationState = Object.freeze({
  pending: false,
  streamingText: "",
  error: null,
  statusMessage: null,
  citations: null,
});

const NOTIFY_THROTTLE_MS = 50;

interface GenerationRun {
  controller: AbortController;
  queryClient: QueryClient;
  outputId: string;
}

const entries = new Map<string, GenerationState>();
const runs = new Map<string, GenerationRun>();
const listeners = new Set<() => void>();
let notifyHandle: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (notifyHandle !== null) {
    clearTimeout(notifyHandle);
    notifyHandle = null;
  }
  for (const listener of listeners) listener();
}

function notify(immediate: boolean) {
  if (immediate) {
    flush();
    return;
  }
  if (notifyHandle !== null) return;
  notifyHandle = setTimeout(flush, NOTIFY_THROTTLE_MS);
}

export function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function getGenerationState(sectionId: string): GenerationState {
  return entries.get(sectionId) ?? IDLE;
}

export function isGenerating(sectionId: string): boolean {
  return entries.get(sectionId)?.pending ?? false;
}

function patchEntry(sectionId: string, patch: Partial<GenerationState>, { immediate = false } = {}) {
  const prev = entries.get(sectionId) ?? IDLE;
  entries.set(sectionId, { ...prev, ...patch });
  notify(immediate);
}

function clearEntry(sectionId: string) {
  if (!entries.delete(sectionId)) return;
  notify(true);
}

export interface StartGenerationParams {
  sectionId: string;
  outputId: string;
  queryClient: QueryClient;
}

export async function startGeneration(params: StartGenerationParams): Promise<void> {
  const { sectionId, outputId, queryClient: qc } = params;
  if (isGenerating(sectionId)) return;

  const controller = new AbortController();
  runs.set(sectionId, { controller, queryClient: qc, outputId });
  patchEntry(sectionId, { pending: true, streamingText: "", error: null, statusMessage: null, citations: null }, { immediate: true });

  try {
    await streamGenerate(
      sectionId,
      {
        onDelta: (text) => {
          const prev = getGenerationState(sectionId);
          patchEntry(sectionId, { streamingText: prev.streamingText + text, statusMessage: null });
        },
        onCitations: (items) => {
          patchEntry(sectionId, { citations: items }, { immediate: true });
        },
        onSection: (section) => {
          // Optimistically update the React Query cache with the persisted section.
          qc.setQueryData(draftKeys.sections(outputId), (prev: unknown[] | undefined) =>
            prev?.map((s: Record<string, unknown>) => (s.id === sectionId ? section : s)),
          );
          void qc.invalidateQueries({ queryKey: draftKeys.sections(outputId) });
          // Keep the generation entry alive (pending + streamingText) so the
          // SectionCard still shows the streamed text while the query-cache
          // update propagates through FirstDraft → DraftCanvas → SectionCard
          // props.  onDone will schedule the cleanup after a short delay.
        },
        onStatus: (data) => {
          if (data.message) patchEntry(sectionId, { statusMessage: data.message }, { immediate: true });
        },
        onError: (detail) => {
          // Keep streamingText so the SectionCard can show it as a fallback
          // (the user shouldn't lose visible text just because the persist failed).
          patchEntry(sectionId, { pending: false, statusMessage: null, error: detail }, { immediate: true });
        },
        onDone: () => {
          const state = entries.get(sectionId);
          if (!state?.pending) return;
          // Delay clearing so React has time to propagate the query-cache
          // update from onSection before we drop the streaming text fallback.
          setTimeout(() => clearEntry(sectionId), 150);
        },
      },
      { signal: controller.signal },
    );
  } catch (err) {
    if (controller.signal.aborted) return;
    const message = err instanceof Error ? err.message : "Generation failed";
    patchEntry(sectionId, { pending: false, statusMessage: null, error: message }, { immediate: true });
  } finally {
    runs.delete(sectionId);
  }
}

export function stopGeneration(sectionId: string): void {
  const run = runs.get(sectionId);
  if (run) run.controller.abort();
  clearEntry(sectionId);
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useSectionGeneration(sectionId: string): GenerationState {
  return useSyncExternalStore(subscribe, () => getGenerationState(sectionId), () => IDLE);
}

export function useIsGenerating(sectionId: string): boolean {
  return useSyncExternalStore(subscribe, () => isGenerating(sectionId), () => false);
}
